import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, User, Calendar, Sparkles, Bot, Brain, CheckCircle, Zap, Target } from 'lucide-react';
import { Link } from 'react-router-dom';
import '../../styles/blog.css';

const BlogPost2 = () => {
  return (
    <div className="blog-post-page">
      {/* Hero Section */}
      <section className="blog-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="blog-hero-content"
          >
            <Link to="/resources" className="back-link">
              <ArrowLeft size={20} />
              Back to Resources
            </Link>
            
            <div className="blog-category">Artificial Intelligence</div>
            
            <h1 className="blog-title">
              The Power of Generative AI and Agentic AI: From Automation to Intelligent Decision-Making
            </h1>
            
            <div className="blog-meta">
              <span className="meta-item">
                <User size={16} />
                Abhishek Nishis
              </span>
              <span className="meta-item">
                <Calendar size={16} />
                December 20, 2024
              </span>
              <span className="meta-item">
                <Clock size={16} />
                10 min read
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="blog-content-section">
        <div className="container">
          <div className="blog-content">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {/* Introduction */}
              <div className="blog-intro">
                <h2 className="intro-heading">AI Beyond Automation</h2>
                <p className="lead-text">
                  Artificial Intelligence (AI) is no longer just a tool for automating routine tasks—it is now capable 
                  of thinking, reasoning, and making decisions independently. While automation has existed for decades, 
                  the emergence of Generative AI and Agentic AI is redefining what businesses can achieve.
                </p>
                
                <div className="highlight-box">
                  <p><Sparkles className="inline-icon" /> <strong>Generative AI</strong> can create content—text, images, code, and even music.</p>
                  <p><Bot className="inline-icon" /> <strong>Agentic AI</strong> can act autonomously—making decisions, solving problems, and executing multi-step processes without human input.</p>
                </div>
                
                <p>
                  Together, these AI technologies are transforming business intelligence, customer service, supply chain management, 
                  finance, and more. This blog explores their differences, business impact, and a real-world case study where 
                  Agentic AI optimized financial decision-making for an investment firm.
                </p>
              </div>

              {/* Generative AI Section */}
              <div className="blog-section">
                <h2><Sparkles className="section-icon" /> What is Generative AI?</h2>
                
                <p>
                  Generative AI refers to AI models that generate new content based on patterns learned from massive datasets. 
                  It powers tools like ChatGPT, DALL·E, and GitHub Copilot, revolutionizing:
                </p>

                <div className="applications-grid">
                  <div className="app-card">
                    <h4>Design & Creativity</h4>
                    <p>AI-generated images, videos, and artwork</p>
                  </div>
                  <div className="app-card">
                    <h4>Software Development</h4>
                    <p>AI-assisted coding and bug detection</p>
                  </div>
                  <div className="app-card">
                    <h4>Marketing & Content Creation</h4>
                    <p>AI-generated blogs, social media posts, and ad copies</p>
                  </div>
                  <div className="app-card">
                    <h4>Customer Support</h4>
                    <p>Automated responses and chatbots</p>
                  </div>
                </div>

                <div className="limitation-box">
                  <h4>However, Generative AI has limitations:</h4>
                  <ul>
                    <li><strong>It lacks real-time adaptability</strong> – It generates content based on past data, not changing situations.</li>
                    <li><strong>It doesn't take action</strong> – It provides outputs but doesn't execute decisions.</li>
                  </ul>
                  <p className="highlight-text">
                    This is where Agentic AI steps in—AI that can reason, decide, and act without constant human input.
                  </p>
                </div>
              </div>

              {/* Agentic AI Section */}
              <div className="blog-section">
                <h2><Bot className="section-icon" /> What is Agentic AI?</h2>
                
                <p>
                  Agentic AI represents the next evolution of AI—moving beyond content generation to independent 
                  decision-making and task execution.
                </p>

                <div className="feature-card">
                  <h3>Key Capabilities of Agentic AI:</h3>
                  <ul className="feature-list">
                    <li><Target size={18} /> <strong>Goal-Oriented:</strong> Works towards specific objectives, adjusting strategies as needed</li>
                    <li><Zap size={18} /> <strong>Autonomy:</strong> Can plan and execute complex tasks without human intervention</li>
                    <li><Brain size={18} /> <strong>Multi-Step Reasoning:</strong> Manages long, complex workflows and adjusts based on feedback</li>
                    <li><CheckCircle size={18} /> <strong>Adaptive Learning:</strong> Continuously improves decisions based on real-time data</li>
                  </ul>
                </div>

                <div className="use-case-box">
                  <h3>Where is Agentic AI Used?</h3>
                  <div className="use-case-grid">
                    <div className="use-case-item">
                      <h4>Finance</h4>
                      <p>AI-driven investment management, fraud detection, and risk assessment</p>
                    </div>
                    <div className="use-case-item">
                      <h4>Supply Chain</h4>
                      <p>Autonomous inventory management and shipment optimization</p>
                    </div>
                    <div className="use-case-item">
                      <h4>Business Intelligence</h4>
                      <p>AI agents analyzing data, predicting trends, and making real-time recommendations</p>
                    </div>
                    <div className="use-case-item">
                      <h4>Healthcare</h4>
                      <p>AI-powered diagnostics, personalized treatments, and administrative automation</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Comparison Section */}
              <div className="blog-section">
                <h2>How is it Different from Generative AI?</h2>
                
                <div className="comparison-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Feature</th>
                        <th>Generative AI</th>
                        <th>Agentic AI</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><strong>Primary Function</strong></td>
                        <td>Creates content (text, images, code)</td>
                        <td>Takes actions, makes decisions</td>
                      </tr>
                      <tr>
                        <td><strong>Adaptability</strong></td>
                        <td>Based on past training data</td>
                        <td>Learns from real-time data</td>
                      </tr>
                      <tr>
                        <td><strong>Decision-Making</strong></td>
                        <td>Provides outputs</td>
                        <td>Acts on insights and executes plans</td>
                      </tr>
                      <tr>
                        <td><strong>Dependency</strong></td>
                        <td>Requires user prompts</td>
                        <td>Operates with minimal input</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <p className="highlight-text">
                  Agentic AI enhances Generative AI by not just generating information, but acting on it.
                </p>
              </div>

              {/* Case Study Section */}
              <div className="blog-section case-study">
                <h2>Case Study: Agentic AI to improve recommendation in an EdTech platform</h2>
                
                <div className="case-study-card">
                  <h3>The Challenge:</h3>
                  <p>
                    The client is one of the leading providers of skill-based training with a modern web-based platform 
                    and millions of users worldwide. They were struggling to generate personalized training based on the 
                    user's preferences, history, and performance.
                  </p>
                  <p>
                    Gen AI platforms could predict training but were not able to provide additional context. 
                    The firm needed an autonomous AI-driven training strategy to optimize decision-making.
                  </p>
                </div>

                <div className="case-study-card solution">
                  <h3>The Solution: An Agentic AI-Powered Training System</h3>
                  <p>
                    To solve these challenges, the firm implemented an Agentic AI-driven training engine. 
                    This system used multiple AI agents working together:
                  </p>
                  
                  <div className="agent-cards">
                    <div className="agent-card">
                      <div className="agent-number">1</div>
                      <h4>Learning System Agent</h4>
                      <p>RAG providing the metadata, feedback and reviews for various learning modules</p>
                    </div>
                    <div className="agent-card">
                      <div className="agent-number">2</div>
                      <h4>Customer Profile Agent</h4>
                      <p>RAG providing a real-time customer profile with their preferences</p>
                    </div>
                  </div>
                  
                  <p className="highlight-text">
                    Unlike traditional AI tools, this Agentic AI system acted independently, continuously learning 
                    from ongoing training and adjusting recommendations in real time.
                  </p>
                </div>
              </div>

              
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost2;